import { v4 as uuidv4 } from 'uuid';
import Paging from '../../core/Paging';
import Warning from '../../core/Warning';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import { makeCancelable, sortByAnyProp, sortObjects } from '../../core/utils/utils';
import { CONCEPT_LOAD_OPTIONS } from '../../core/utils/constants';
import GS from '../../grafsync/grafsync';
import Concept from './Concept';

const RESOURCE_BATCH = 100;
const PAGE_SIZE = 500;
const MAX_NODES = 5000;

const loadOptions = {
  predicates: [
    "http://www.w3.org/2004/02/skos/core#inScheme",
    "http://www.w3.org/2004/02/skos/core#prefLabel",
    "http://www.w3.org/2002/07/owl#deprecated",
    "http://purl.org/dc/terms/title",
    "http://www.w3.org/2008/05/skos-xl#prefLabel",
    "http://www.w3.org/2004/02/skos/core#narrower",
    "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
    "https://grafsync.graphifi.com/schema/gs/status"
  ],
  includeAuth: true,
  tests: [
    {
      id: "http://www.w3.org/2008/05/skos-xl#prefLabel",
      join: [{
        predicate: "http://www.w3.org/2008/05/skos-xl#literalForm"
      }]
    }
  ]
};

class TaxonomyStructure extends Component {

  constructor(props) {
    super(props);
    this.state = {
      showNumber: RESOURCE_BATCH,
      concepts: [],
      loadingPage: 0,
      loadingID: undefined,
      warning: "",
      viewLang: null,
      useCustomSort: props.useCustomSort,
      page: 1,
      jumpToPageTime: null,
      hasScrolled: false,
      isLoading: true,
      isLoaded: false,
      isErrored: null
    };
    this.cancellable = null;
  }

  componentDidMount() {
    this.collectData();
  }

  timeMoreConcepts = () => {
    if (this.moreTimeout) clearTimeout(this.moreTimeout);
    if (this.state.showNumber < this.state.concepts.length && this.state.showNumber < PAGE_SIZE) {
      this.moreTimeout = setTimeout(() => {
        this.showMore();
      }, 900);
    }
  };

  static getDerivedStateFromProps(props, state) {
    if ((props.concepts && props.concepts.length > 0 && props.data._version !== state.version)
      || (props.viewLang !== state.viewLang)
      || (props.useCustomSort !== state.useCustomSort)
      || (props.foundPath && props.foundTime && props.foundTime !== state.jumpToPageTime)) {
      let page = state.page;
      if (props.foundPath && props.foundTime !== state.jumpToPageTime) {
        // Make sure we do this on filtered nodes for this taxonomy
        const concepts = state.concepts;
        const fiActive = concepts.findIndex(item => item === props.activeConcept);
        if (fiActive >= 0) {
          page = Math.floor(fiActive / PAGE_SIZE) + 1;
        }
        else {
          const fiTitle = concepts.findIndex(item => props.foundPath.includes("<" + item + ">"));
          if (fiTitle >= 0) {
            page = Math.floor(fiTitle / PAGE_SIZE) + 1;
          }
        }
      }

      // If a deletion happens that reduces page count and we're on the last page we reset page to 1
      const pageCount = Math.floor((props.data[props.childProperty].length + PAGE_SIZE - 1) / PAGE_SIZE);
      if (page > pageCount) page = 1;

      return {
        isLoading: true,
        isLoaded: false,
        loadingID: uuidv4(),
        page,
        jumpToPageTime: props.foundTime,
        viewLang: props.viewLang,
        useCustomSort: props.useCustomSort,
        ...(props.viewLang !== state.viewLang) && { concepts: [] },
        version: props.data._version
      };
    }
    return null;
  }

  componentDidUpdate() {
    if (!this.state.isLoaded && this.state.loadingID !== undefined) this.collectData();
    this.timeMoreConcepts();
  }

  async collectData() {
    // We load ALL the resources necessary at this level in the tree in one API call to avoid unnecessary calls
    if (this.props.data[this.props.childProperty] && this.props.data[this.props.childProperty].length > 0) {
      if (!this.state.isLoaded && !this.state.isErrored) {
        // There may be more than 5000 children so we need to load in pages just in case!
        // We need to load everything because at this point we don't know which belong to this taxonomy
        const loadingID = this.state.loadingID;
        try {
          const pages = Math.floor((this.props.data[this.props.childProperty].length + MAX_NODES - 1) / MAX_NODES);
          for (let page = 0; page < pages; page++) {
            if (this.state.loadingID !== loadingID) {
              return;
            }
            const pageStart = page * MAX_NODES;
            const pageEnd = (page + 1) * MAX_NODES;
            const pageChildren = this.props.data[this.props.childProperty].slice(pageStart, pageEnd);
            await this.loadData(pageChildren).promise;
            if (this.cancellable && this.cancellable.isCancelled()) return;
          }
          // Once all loaded then we sort
          this.sortConcepts();
        }
        catch (e) {
          if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
        }
      }
    }
    else {
      if ((this.cancellable && !this.cancellable.isCancelled()) || !this.cancellable)
        this.setState({ isLoading: false, isLoaded: true });
    }
  }

  sortConcepts(impactsLoading = true) {
    const concepts = this.props.concepts.map(id => GS.data(id, this.props.graph));
    const firstLang = this.props.viewLang || this.props.defaultLang;
    const secondLang = this.props.defaultLang;
    // GS.data returns clones so we can sort on those object without affecting GS internal storage
    let sortedConcepts;
    if (this.props.useCustomSort === true) {
      // We sort be prefLabel first to get in the right order for duplicate custom sort values
      try {
        sortObjects(concepts, 'prefLabel', firstLang, secondLang, false);
        sortedConcepts = sortByAnyProp(concepts, this.props.customSort.property, this.props.customSort.sortNumerically, firstLang)
          .map(concept => concept.id);
      }
      catch (e) {
        this.setState({ isErrored: e });
      }
    }
    else {
      sortedConcepts = sortObjects(concepts, 'prefLabel', firstLang, secondLang, false)
        .map(concept => concept.id);
    }
    const isSortEqual = this.state.concepts && sortedConcepts.length === this.state.concepts.length
      && sortedConcepts.every((s, index) => this.state.concepts[index] === s);
    if (isSortEqual && !impactsLoading) {
      // Basically the sort doesn't change anything so don't cause a reload
      return;
    }
    else {
      let page = this.state.page;
      if (this.props.foundPath) {
        const fiActive = sortedConcepts.findIndex(item => item === this.props.activeConcept);
        if (fiActive >= 0) {
          page = Math.floor(fiActive / PAGE_SIZE) + 1;
        }
        else {
          const fiTitle = sortedConcepts.findIndex(item => this.props.foundPath.includes("<" + item + ">"));
          if (fiTitle >= 0) page = Math.floor(fiTitle / PAGE_SIZE) + 1;
        }
      }
      this.setState({ page, isLoading: false, isLoaded: true, concepts: sortedConcepts, showNumber: RESOURCE_BATCH });
    }
  }

  loadData(concepts) {
    const { viewLang, defaultLang, graph, useCustomSort } = this.props;
    const assessedLoadOptions = useCustomSort ? {} : loadOptions;
    const mergedLoadOptions = Object.assign({}, assessedLoadOptions, { language: viewLang && viewLang !== defaultLang ? [defaultLang, viewLang] : defaultLang });
    this.cancellable = makeCancelable(GS.loadData(concepts, mergedLoadOptions, graph, this.props.skosXL));
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
    if (this.moreTimeout) clearTimeout(this.moreTimeout);
  }

  monitorConceptUpdate = (iri, property) => {
    if (property === "prefLabel" ||
      (this.props.useCustomSort && property === this.props.customSort.property)) this.sortConcepts(false);
  };

  showMore = () => {
    const showNumber = this.state.showNumber + RESOURCE_BATCH;
    this.setState({ showNumber });
  };

  handlePageChange = async (page = 1) => this.setState({ page, showNumber: RESOURCE_BATCH });

  showWarning = (warning) => this.setState({ warning });

  onExitWarning = () => this.setState({ warning: "" });

  render() {

    const { warning, isLoading, concepts, isErrored, showNumber, page } = this.state;
    const { onSelect, defaultLang, schemeIRI, expandConcept, collapseConcept, foundRef, addToScheme, useCounterIRI, counters,
      activeConcept, useXL, useSlashIRI, useUuidIRI, iriOptions, useWorkflow, useCustomSort, customSort,
      onAddExistingConcept, onAddNewConcept, onDeleteConcept, foundTime, titleTree, viewLang, graph, titlePath, selectedTitlePath,
      childProperty, foundPath, onBreakLink, data, path, expandedPaths, isReadOnly, skosXL, isFullView } = this.props;

    if (isErrored) return <Errored data={isErrored} />;

    if (isLoading && concepts.length === 0) return <Loading offset="20px" message={this.props.concepts && this.props.concepts.length > 1000 ?
      <span>This may take a few seconds.<br />There are {this.props.concepts.length} concepts to check.<br />They only get checked once.</span>
      :
      null} />;

    const pageCount = Math.floor((concepts.length + PAGE_SIZE - 1) / PAGE_SIZE);
    const startNode = (page - 1) * PAGE_SIZE;

    const loadOptions = useCustomSort ?
      {
        language: viewLang && viewLang !== defaultLang ? [defaultLang, viewLang] : defaultLang
      }
      :
      Object.assign({}, CONCEPT_LOAD_OPTIONS, { language: viewLang && viewLang !== defaultLang ? [defaultLang, viewLang] : defaultLang });

    return (
      <React.Fragment>
        {warning !== "" && <Warning message={warning} title="Action Not Permitted" onExitWarning={this.onExitWarning} />}
        {pageCount > 1 &&
          <div>
            <Paging
              pageItemsRange={isFullView ? 3 : 1}
              showText={false}
              page={page}
              pageCount={pageCount}
              handlePageChange={this.handlePageChange} />
          </div>
        }
        {concepts.slice(startNode, startNode + showNumber).map(concept => {
          // Filter paths to only send those relevant to each concept
          // We include customSort in key to force computation of sort value
          const expandedSubpaths = expandedPaths.filter(subPath => subPath.startsWith(path + "<" + concept + ">"));
          return <Concept
            key={concept + useCustomSort}
            id={concept}
            graph={graph}
            activeConcept={activeConcept}
            onSelect={onSelect}
            isFullView={isFullView}
            useCustomSort={useCustomSort}
            customSort={customSort}
            selectedTitlePath={selectedTitlePath}
            titlePath={titlePath}
            titleTree={titleTree}
            iriOptions={iriOptions}
            skosXL={skosXL}
            showWarning={this.showWarning}
            useXL={useXL}
            useWorkflow={useWorkflow}
            useSlashIRI={useSlashIRI}
            useUuidIRI={useUuidIRI}
            useCounterIRI={useCounterIRI}
            counters={counters}
            expandedPaths={expandedSubpaths}
            path={path + "<" + concept + ">"}
            addToScheme={addToScheme}
            onBreakLink={onBreakLink}
            childProperty={childProperty}
            onAddNewConcept={onAddNewConcept}
            onDeleteConcept={onDeleteConcept}
            onAddExistingConcept={onAddExistingConcept}
            expandConcept={expandConcept}
            collapseConcept={collapseConcept}
            parent={data.id}
            isReadOnly={isReadOnly}
            foundRef={foundRef}
            foundTime={foundTime}
            foundPath={foundPath}
            schemeIRI={schemeIRI}
            defaultLang={defaultLang}
            viewLang={viewLang}
            loadOptions={loadOptions}
            updateEvent={this.monitorConceptUpdate}
          />;
        }
        )}
        {pageCount > 1 &&
          <div>
            <Paging
              pageItemsRange={isFullView ? 3 : 1}
              showText={false}
              page={page}
              pageCount={pageCount}
              handlePageChange={this.handlePageChange} />
          </div>
        }
      </React.Fragment>
    );
  }

}

TaxonomyStructure.defaultProps = {
  useCustomSort: false
};

TaxonomyStructure.propTypes = {
  schemeIRI: PropTypes.string.isRequired,
  defaultLang: PropTypes.string.isRequired,
  viewLang: PropTypes.string,
  version: PropTypes.number,
  path: PropTypes.string.isRequired,
  isReadOnly: PropTypes.bool,
  expandedPaths: PropTypes.arrayOf(PropTypes.string).isRequired,
  expandConcept: PropTypes.func.isRequired,
  collapseConcept: PropTypes.func.isRequired,
  onAddExistingConcept: PropTypes.func.isRequired,
  onAddNewConcept: PropTypes.func.isRequired,
  onDeleteConcept: PropTypes.func.isRequired,
  selectedTitlePath: PropTypes.array,
  foundTime: PropTypes.number,
  skosXL: PropTypes.bool.isRequired,
  useXL: PropTypes.bool,
  useWorkflow: PropTypes.bool,
  useSlashIRI: PropTypes.bool,
  useUuidIRI: PropTypes.bool,
  useCounterIRI: PropTypes.bool,
  counters: PropTypes.object,
  useCustomSort: PropTypes.bool,
  customSort: PropTypes.object,
  foundPath: PropTypes.string,
  childProperty: PropTypes.string.isRequired,
  showWarning: PropTypes.func,
  graph: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  foundRef: PropTypes.object,
  isFullView: PropTypes.bool,
  titleTree: PropTypes.array,
  concepts: PropTypes.arrayOf(PropTypes.string),
  onSelect: PropTypes.func.isRequired,
  addToScheme: PropTypes.func.isRequired,
  onBreakLink: PropTypes.func.isRequired,
  iriOptions: PropTypes.object
};

export default TaxonomyStructure;